/**
 * Afrikaans: Billing
 *
 * What this file covers: Credits, plans, top-ups, the ledger, usage, alerts and invoices.
 *
 * Tone: Exact about money. Credits are what is spent; R is used only for rand actually paid. Never use words
 * like margin, markup or profit. Write amounts and dates the way the language writes them.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const billing: Catalog = {
  'billing.currency': 'Geldeenheid',
  'billing.description': 'Jou beursiesaldo, transaksiegeskiedenis en herlaaie.',
  'billing.loadError': 'Die beursie kon nie gelaai word nie.',
  'billing.ledgerError': 'Onlangse transaksies kon nie gelaai word nie.',
  'billing.empty.title': 'Jou beursie is leeg',
  'billing.empty.body': 'Stuur is geblokkeer totdat jy oplaai. Agente wat in die inboks antwoord sal “Nie genoeg krediet om te stuur” sien, en uitsendings sal nie begin nie.',
  'billing.lowBalance.bodyWithCount': 'Ongeveer {{count}} boodskappe oor teen jou onlangse tempo.',
  'billing.noTransactions.hint': 'Oplaaie en boodskapkoste verskyn hier soos dit gebeur.',
  'billing.roughlyMessages': 'Ongeveer {{count}} boodskappe',
  'billing.perMessage': '{{amount}} per boodskap onlangs',
  'billing.credit': 'krediet',
  'billing.debit': 'debiet',
  'billing.pastDue.title': 'Jou intekening is agterstallig',
  'billing.pastDue.body': 'Jou plan se maandelikse heffing het nie op jou kaart deurgegaan nie. Werk jou kaart by en ons probeer binne die uur weer. Funksies bly voorlopig aan, maar die plan eindig as dit nog onbetaal is wanneer die grasietydperk verstryk.',
  'billing.pastDue.action': 'Werk jou kaart by',
  'billing.invoices': 'Fakture',
  'billing.invoices.empty': 'Nog geen fakture nie',
  'billing.invoices.empty.hint': 'Fakture word aan die einde van elke rekeningperiode uitgereik.',
  'billing.invoices.notIssued': 'Nog nie uitgereik nie',
  'billing.invoiceStatus.draft': 'Konsep',
  'billing.invoiceStatus.issued': 'Uitgereik',
  'billing.invoiceStatus.paid': 'Betaal',
  'billing.invoiceStatus.void': 'Nietig',
  'billing.fullLedger': 'Volle grootboek →',
  'billing.ledger.columns.when': 'Wanneer',
  'billing.ledger.description': 'Elke krediet en debiet op jou beursie.',
  'billing.ledger.loadError': 'Kon nie die grootboek laai nie.',
  'billing.ledger.noMatch': 'Geen grootboekinskrywings pas nie.',
  'billing.ledger.searchPlaceholder': 'Soek beskrywing / tipe…',
  'billing.ledger.title': 'Grootboek',
  'billing.ledger.entries': 'Inskrywings',
  'billing.ledger.creditedTotal': 'Bygevoeg',
  'billing.ledger.debitedTotal': 'Bestee',
  'billing.ledger.amount': 'Bedrag',
  'billing.ledger.type': 'Tipe',
  'billing.ledger.type.any': 'Enige tipe',
  'billing.ledger.sku': 'Waarvoor',
  'billing.ledger.range': 'Tydperk',
  'billing.ledger.range.all': 'Alle tyd',
  'billing.ledger.range.7d': 'Laaste 7 dae',
  'billing.ledger.range.30d': 'Laaste 30 dae',
  'billing.ledger.range.90d': 'Laaste 90 dae',
  'billing.ledger.search': 'Soek transaksies',
  'billing.ledger.search.placeholder': 'Soek beskrywing of tipe',
  'billing.ledger.emptyFiltered': 'Niks in hierdie tydperk nie',
  'billing.ledger.exported': 'Grootboek afgelaai.',
  'billing.entryType.topup': 'Oplaai',
  'billing.entryType.grant': 'Toegeken',
  'billing.entryType.debit': 'Boodskapkoste',
  'billing.entryType.refund': 'Terugbetaling',
  'billing.entryType.adjustment': 'Aanpassing',
  'billing.entryType.plan': 'Plankoste',
  'billing.lowBalance': 'Lae saldo',
  'billing.emptyBalance': 'Geen krediet',
  'billing.lowBalance.body': 'Jou beursie raak leeg. Laai op om aan te hou boodskappe stuur.',
  'billing.noTransactionsYet': 'Nog geen transaksies nie.',
  'billing.recentTransactions': 'Onlangse transaksies',
  'billing.topUp': 'Laai op →',
  'billing.topUpNow': 'Laai nou op →',
  'billing.topup.description': 'Koop krediete. Een krediet is een rand se boodskappe; naira en sjielings word teen die huidige koers omgeskakel.',
  'billing.topup.amount': 'Hoeveel om te betaal ({{currency}})',
  'billing.topup.custom': 'Of ŉ ander bedrag',
  'billing.topup.custom.hint': 'Tussen {{min}} en {{max}}.',
  'billing.topup.tooSmall': 'Die kleinste oplaai is {{min}}.',
  'billing.topup.tooLarge': 'Die grootste oplaai op een slag is {{max}}. Kontak ons vir meer.',
  'billing.topup.afterTopUp': 'Ná hierdie oplaai',
  'billing.topup.buys': '{{amount}} koop {{credits}}, ongeveer {{count}} boodskappe teen jou onlangse koers.',
  'billing.topup.provider': 'Betaal met',
  'billing.topup.provider.unavailable': '{{name}} (nog nie beskikbaar nie)',
  'billing.topup.noProvider.title': 'Geen betaalmetode beskikbaar nie',
  'billing.topup.noProvider.body': 'Ons kan nog nie {{currency}}-betalings aanlyn hanteer nie. Kontak ondersteuning om op te laai.',
  'billing.topup.unknownCurrency': 'Jou beursie is in {{currency}}, wat geen voorafbedrae het nie. Voer een hieronder in.',
  'billing.topup.confirm.title': 'Gaan na die betaalbladsy?',
  'billing.topup.confirm.body': 'Jy verlaat RepletoryReach om {{amount}} met {{provider}} te betaal.',
  'billing.topup.confirm.hint': 'Jou saldo werk by sodra die betaling deurgaan, gewoonlik onmiddellik.',
  'billing.topup.confirm.go': 'Gaan voort om te betaal',
  'billing.topup.starting': 'Stel die betaling op…',
  'billing.topup.redirecting': 'Neem jou na die betaalbladsy…',
  'billing.topup.returned': 'Welkom terug. As die betaling deurgegaan het, is jou saldo reeds bygewerk.',
  'billing.topup.reference': 'Betalingsverwysing: {{reference}}. Haal dit aan as jy oor hierdie betaling moet navraag doen.',
  'billing.topup.notConfigured': 'Aanlynbetalings is nog nie op hierdie rekening opgestel nie. Kontak ondersteuning om op te laai.',
  'billing.topup.providerError': 'Die betaalverskaffer kon nie bereik word nie. Probeer binnekort weer.',
  'billing.topup.unknownProvider': 'Daardie betaalmetode is nie beskikbaar nie.',
  'billing.topup.howItWorks': 'Hoe oplaaie werk',
  'billing.topup.howItWorks.body': 'Krediete word by jou beursie gevoeg sodra die betaling deur is: een krediet vir elke rand betaal, en naira of sjielings teen die huidige koers. Krediete word per boodskap bestee soos jy stuur en verval nie.',
  'billing.topup.failed': 'Kon nie die herlaai begin nie.',
  'billing.topup.noCheckoutUrl': 'Die poort het nie ’n betaal-URL teruggegee nie.',
  'billing.topup.pay': 'Betaal',
  'billing.topup.paymentMethod': 'Betaalmetode',
  'billing.topup.redirectNotice': 'Jy word na {{provider}} herlei om die betaling te voltooi. Jou beursie word gekrediteer sodra die poort bevestig.',
  'billing.topup.topUp': 'Herlaai',
  'billing.walletBalance': 'Beursiesaldo',
  'billing.invoices.view': 'Bekyk die faktuur',
  'billing.invoices.issuedAt': 'Uitgereik',
  'billing.usage.title': 'Gebruik',
  'billing.usage.description': 'Waarheen jou krediet gaan: elke boodskap, botantwoord, modelteken, lêer en kennisgewing, geprys soos dit gebeur het.',
  'billing.usage.period': 'Tydperk',
  'billing.usage.period.month': 'Hierdie maand',
  'billing.usage.period.7d': 'Laaste 7 dae',
  'billing.usage.period.30d': 'Laaste 30 dae',
  'billing.usage.period.90d': 'Laaste 90 dae',
  'billing.usage.period.previous_month': 'Vorige maand',
  'billing.usage.loadError': 'Kon nie gebruik laai nie.',
  'billing.usage.exported': 'Gebruik uitgevoer.',
  'billing.usage.stateHint': 'Saldo {{balance}}, laesaldolyn {{threshold}}.',
  'billing.usage.manageAlerts': 'Waarskuwings en outomatiese aanvulling',
  'billing.usage.kpi.spent': 'Bestee',
  'billing.usage.kpi.spentHint': '{{count}} heffings',
  'billing.usage.kpi.perDay': 'Per dag, gemiddeld',
  'billing.usage.kpi.projected': 'Sowat {{amount}} teen maandeinde teen hierdie pas',
  'billing.usage.kpi.projectedLabel': 'Maandeinde, teen hierdie pas',
  'billing.usage.kpi.runway': 'Saldo hou vir',
  'billing.usage.kpi.runwayDays': '{{count}} dae',
  'billing.usage.kpi.runwayNone': 'Nog niks bestee nie',
  'billing.usage.kpi.runwayHint': 'Teen die tydperk se gemiddelde daaglikse besteding',
  'billing.usage.kpi.lowLine': 'Waarskuwings onder {{amount}}',
  'billing.usage.daily.title': 'Besteding per dag',
  'billing.usage.daily.empty': 'Nog nie genoeg dae nie',
  'billing.usage.daily.emptyHint': 'Die grafiek verskyn sodra daar twee dae se heffings in die tydperk is.',
  'billing.usage.byCategory.title': 'Per soort',
  'billing.usage.byCategory.hint': 'WhatsApp word per boodskap geprys volgens Meta se kategorie en die ontvanger se land; KI per duisend tekens; die res per eenheid soos onder Tariewe gelys.',
  'billing.usage.noSpend': 'Niks gehef in hierdie tydperk nie',
  'billing.usage.noSpendHint': 'Heffings verskyn hier sodra \'n boodskap, botantwoord of lêer deurgaan.',
  'billing.usage.entriesCount': '{{count}} heffings',
  'billing.usage.tabs.activity': 'Per aktiwiteit',
  'billing.usage.tabs.items': 'Per item',
  'billing.usage.tabs.broadcasts': 'Uitsendings',
  'billing.usage.tabs.contacts': 'Kontakte',
  'billing.usage.tabs.flows': 'Vloeie',
  'billing.usage.tabs.rates': 'Tariewe',
  'billing.usage.columns.activity': 'Aktiwiteit',
  'billing.usage.columns.entries': 'Heffings',
  'billing.usage.columns.share': 'Aandeel',
  'billing.usage.columns.item': 'Item',
  'billing.usage.columns.units': 'Eenhede',
  'billing.usage.columns.rate': 'Tarief',
  'billing.usage.columns.per': 'Per',
  'billing.usage.columns.messages': 'Boodskappe',
  'billing.usage.columns.lastActivity': 'Laaste aktiwiteit',
  'billing.usage.ratePer': '{{amount}} per {{unit}}',
  'billing.usage.viewEntries': 'Inskrywings',
  'billing.usage.noBroadcasts': 'Geen uitsendingbesteding in hierdie tydperk nie',
  'billing.usage.noContacts': 'Geen kontakbesteding in hierdie tydperk nie',
  'billing.usage.noFlows': 'Geen vloeibesteding in hierdie tydperk nie',
  'billing.usage.broadcastLink': 'Uitsending {{id}}',
  'billing.usage.flowLink': 'Vloei {{id}}',
  'billing.usage.rates.hint': 'Wat elke eenheid kos, in krediete. Een krediet is een rand se boodskappe. Niks word gehef wat nie op hierdie lys is nie.',
  'billing.usage.rates.empty': 'Geen tariewe gepubliseer nie',
  'billing.usage.rates.converted': 'omgeskakel',
  'billing.usage.drawer.summary': '{{count}} inskrywings',
  'billing.usage.drawer.more': 'Wys meer',
  'billing.usage.thisMonth': 'Hierdie maand',
  'billing.usage.thisMonthHint': 'Besteding tot dusver, die pas, en hoe lank die saldo daarteen hou.',
  'billing.usage.fullUsage': 'Volledige gebruik →',
  'billing.autoTopup.on': 'Outomatiese aanvulling is aan',
  'billing.autoTopup.setUp': 'Stel outomatiese aanvulling op',
  'billing.autoTopup.onHint': 'Outomatiese aanvulling is aan: {{amount}} vanaf {{method}} wanneer die saldo onder jou drempel daal.',
  'billing.autoTopup.offHint': 'Outomatiese aanvulling is af. Jy word gewaarsku onder {{threshold}}; stuur stop by nul.',
  'billing.source.broadcast': 'Uitsending',
  'billing.source.flow': 'Botantwoord',
  'billing.source.agent': 'Agentantwoord',
  'billing.source.inbound': 'Boodskap ontvang',
  'billing.source.message': 'Boodskap gestuur',
  'billing.source.kb': 'Kennisbasis',
  'billing.source.notify': 'Kennisgewing',
  'billing.source.media': 'Media',
  'billing.source.web': 'Webwerfklets',
  'billing.source.topup': 'Aanvulling',
  'billing.source.auto_topup': 'Outomatiese aanvulling',
  'billing.source.manual': 'Aansuiwering',
  'billing.source.plan': 'Plan',
  'billing.source.platform': 'Platform',
  'billing.source.grant': 'Toegestaan',
  'billing.source.refund': 'Terugbetaling',
  'billing.source.adjustment': 'Aansuiwering',
  'billing.source.debit': 'Heffing',
  'billing.category.whatsapp': 'WhatsApp',
  'billing.category.ai': 'KI',
  'billing.category.knowledge': 'Kennisbasis',
  'billing.category.notifications': 'Kennisgewings',
  'billing.category.media': 'Media',
  'billing.category.web': 'Webwerfklets',
  'billing.category.other': 'Ander',
  'billing.unit.message.one': 'boodskap',
  'billing.unit.message.other': 'boodskappe',
  'billing.unit.1k_tokens.one': 'duisend tekens',
  'billing.unit.1k_tokens.other': 'duisend tekens',
  'billing.unit.document.one': 'dokument',
  'billing.unit.document.other': 'dokumente',
  'billing.unit.question.one': 'vraag',
  'billing.unit.question.other': 'vrae',
  'billing.unit.file.one': 'lêer',
  'billing.unit.file.other': 'lêers',
  'billing.unit.gb_day.one': 'GB-dag',
  'billing.unit.gb_day.other': 'GB-dae',
  'billing.unit.call.one': 'oproep',
  'billing.unit.call.other': 'oproepe',
  'billing.unit.topup.one': 'aanvulling',
  'billing.unit.topup.other': 'aanvullings',
  'billing.actor.flows': 'Die bot',
  'billing.actor.broadcasts': '\'n Uitsending',
  'billing.actor.channel-wa': 'Die WhatsApp-kanaal',
  'billing.actor.billing': 'Die platform',
  'billing.actor.media': 'Die mediadiens',
  'billing.actor.user': '\'n Persoon in jou span',
  'billing.explain.title': 'Oor hierdie inskrywing',
  'billing.explain.charged': 'Gehef',
  'billing.explain.credited': 'Gekrediteer',
  'billing.explain.workedOut': 'Bereken as',
  'billing.explain.formula': '{{units}} {{unit}} × {{rate}} per {{per}}',
  'billing.explain.what': 'Wat',
  'billing.explain.contact': 'Kontak',
  'billing.explain.run': 'lopie {{id}}',
  'billing.explain.openConversation': 'Maak die gesprek oop',
  'billing.explain.by': 'Veroorsaak deur',
  'billing.explain.details': 'Besonderhede',
  'billing.explain.footnote': 'Elke syfer kom uit die faktureringsgrootboek soos dit destyds geskryf is; \'n latere tariefverandering verander dit nie.',
  'billing.meta.model': 'Model',
  'billing.meta.direction': 'Rigting',
  'billing.meta.tokens': 'Tekens',
  'billing.meta.purpose': 'Doel',
  'billing.meta.channel': 'Kanaal',
  'billing.meta.template': 'Sjabloon',
  'billing.meta.category': 'Kategorie',
  'billing.meta.country': 'Land',
  'billing.meta.msg_type': 'Boodskaptipe',
  'billing.meta.rate_name': 'Tarief',
  'billing.meta.fx_per_zar': 'Wisselkoers (per ZAR)',
  'billing.meta.quoted_currency': 'Gekwoteer in',
  'billing.meta.bytes': 'Grepe',
  'billing.meta.files': 'Lêers',
  'billing.meta.day': 'Dag',
  'billing.meta.chunks': 'Gedeeltes',
  'billing.meta.characters': 'Karakters',
  'billing.meta.size_bytes': 'Grootte (grepe)',
  'billing.meta.mime': 'Tipe',
  'billing.ledger.for': 'Vir',
  'billing.ledger.for.any': 'Enigiets',
  'billing.ledger.explain': 'Verduidelik',
  'billing.ledger.unitsAtRate': '{{units}} × {{rate}}',
  'billing.alerts.title': 'Waarskuwings en outomatiese aanvulling',
  'billing.alerts.description': 'Word ingelig voordat die krediet opraak, en laat die platform vir jou aanvul.',
  'billing.alerts.loadError': 'Kon nie jou waarskuwinginstellings laai nie.',
  'billing.alerts.saved': 'Instellings gestoor.',
  'billing.alerts.saveFailed': 'Kon nie die instellings stoor nie.',
  'billing.alerts.state.ok': 'Bo jou laesaldolyn',
  'billing.alerts.state.low': 'Onder jou laesaldolyn',
  'billing.alerts.state.empty': 'Leeg: stuur het gestop',
  'billing.alerts.lowLine': 'Laesaldolyn',
  'billing.alerts.lowLine.default': 'Die verstek vir jou geldeenheid',
  'billing.alerts.lowLine.custom': 'Deur jou gestel',
  'billing.alerts.low.title': 'Laesaldowaarskuwing',
  'billing.alerts.low.body': 'Wanneer die saldo onder die lyn daal, word jy per e-pos ingelig (en per WhatsApp as jy \'n nommer gee). By nul word jy weer ingelig, omdat stuur gestop het.',
  'billing.alerts.low.threshold': 'Waarsku my wanneer die saldo daal onder (krediete)',
  'billing.alerts.low.thresholdHint': 'In krediete. Laat leeg vir die verstek, {{amount}}.',
  'billing.alerts.low.email': 'E-pos',
  'billing.alerts.low.emailHint': 'Een of meer adresse, geskei deur kommas. Leeg stuur aan almal in jou organisasie.',
  'billing.alerts.low.msisdn': 'WhatsApp-nommer (opsioneel)',
  'billing.alerts.low.msisdnHint': '\'n Teks vanaf jou eie WhatsApp-nommer. Dit kom slegs aan as daardie nommer jou besigheid in die laaste 24 uur geboodskap het.',
  'billing.alerts.low.inApp': 'Wys waarskuwings in die konsole',
  'billing.alerts.low.inAppHint': 'Die sybalk en die faktureringsbladsy word amber wanneer laag en rooi wanneer leeg.',
  'billing.alerts.test.action': 'Stuur \'n toetswaarskuwing',
  'billing.alerts.test.sentTo': 'Toetswaarskuwing gestuur aan {{target}}.',
  'billing.alerts.test.recorded': 'Toetswaarskuwing aangeteken. E-pos: {{detail}}',
  'billing.alerts.test.failed': 'Die toetswaarskuwing kon nie gestuur word nie.',
  'billing.alerts.auto.title': 'Outomatiese aanvulling',
  'billing.alerts.auto.body': 'Wanneer die saldo onder die drempel daal, hef die platform jou gestoorde kaart vir die bedrag hieronder, hoogstens soveel keer per dag as wat jy toelaat.',
  'billing.alerts.auto.enable': 'Vul outomaties aan',
  'billing.alerts.auto.needsCard': 'Stoor eers \'n kaart hieronder.',
  'billing.alerts.auto.threshold': 'Laai op wanneer die saldo daal onder (krediete)',
  'billing.alerts.auto.thresholdHint': 'Leeg gebruik die laesaldolyn.',
  'billing.alerts.auto.amount': 'Bedrag om te betaal ({{currency}})',
  'billing.alerts.auto.amountHint': 'Elke keer van die gestoorde kaart gehef. As dit minder as die drempel is, hef die platform weer totdat die saldo bo die lyn is, tot by die daaglikse perk.',
  'billing.alerts.auto.cap': 'Hoogstens, per dag',
  'billing.alerts.auto.capHint': 'Beskerm jou teen \'n weghol-uitsending. {{count}} tot dusver vandag.',
  'billing.alerts.auto.method': 'Kaart om te hef',
  'billing.alerts.auto.methodPrimary': 'Die primêre kaart',
  'billing.alerts.auto.methodHint': 'Die kaart word sonder jou teenwoordigheid gehef, deur die poort wat dit gestoor het.',
  'billing.alerts.auto.chargeNow': 'Vul nou aan',
  'billing.alerts.auto.chargeNowBody': 'Hef die gestoorde kaart nou {{amount}}?',
  'billing.alerts.auto.charged': '{{amount}} gehef. Die saldo is {{balance}}.',
  'billing.alerts.auto.notCharged': 'Nie gehef nie',
  'billing.alerts.auto.lastHint': 'Laaste poging {{when}}: {{outcome}}',
  'billing.alerts.auto.succeeded': 'geslaag',
  'billing.alerts.auto.failed': 'misluk',
  'billing.alerts.auto.never': 'Nog nooit geloop nie',
  'billing.alerts.methods.title': 'Gestoorde kaarte',
  'billing.alerts.methods.body': 'Die kaartnommer bereik ons nooit nie: die poort hou dit en gee ons \'n teken om te hef. Om \'n kaart te stoor is \'n klein heffing wat op jou beursie gekrediteer word.',
  'billing.alerts.methods.add': 'Stoor \'n kaart',
  'billing.alerts.methods.empty': 'Geen gestoorde kaarte nie',
  'billing.alerts.methods.emptyHint': 'Stoor een om outomatiese aanvulling aan te skakel.',
  'billing.alerts.methods.primary': 'Primêr',
  'billing.alerts.methods.primaryLabel': '{{label}} (primêr)',
  'billing.alerts.methods.notReusable': 'Kan nie weer gehef word nie',
  'billing.alerts.methods.expires': 'Verval {{month}}/{{year}}',
  'billing.alerts.methods.added': 'gestoor {{when}}',
  'billing.alerts.methods.makePrimary': 'Maak primêr',
  'billing.alerts.methods.remove.title': 'Vergeet hierdie kaart?',
  'billing.alerts.methods.remove.body': '{{label}} sal nie weer gehef word nie. Outomatiese aanvulling skakel af as dit die laaste kaart was.',
  'billing.alerts.methods.autoOff': 'Outomatiese aanvulling is af: daar is geen kaart oor om te hef nie.',
  'billing.alerts.methods.returned': 'Welkom terug. Die kaart verskyn hier sodra die poort dit bevestig, gewoonlik binne \'n paar sekondes.',
  'billing.alerts.methods.noUrl': 'Die poort het nie \'n betaalbladsy teruggestuur nie.',
  'billing.alerts.methods.startFailed': 'Kon nie begin om \'n kaart te stoor nie.',
  'billing.alerts.methods.confirm.title': 'Gaan na die betaalbladsy?',
  'billing.alerts.methods.confirm.body': 'Jy verlaat RepletoryReach om die kaart by Paystack in te voer. \'n Heffing van {{amount}} stoor dit en word op jou beursie gekrediteer.',
  'billing.alerts.methods.confirm.go': 'Gaan voort na Paystack',
  'billing.alerts.history.title': 'Waarskuwinggeskiedenis',
  'billing.alerts.history.body': 'Elke waarskuwing gestuur, per kanaal, met wat daarmee gebeur het.',
  'billing.alerts.history.empty': 'Nog geen waarskuwings gestuur nie',
  'billing.alerts.history.kind': 'Waarskuwing',
  'billing.alerts.history.channel': 'Kanaal',
  'billing.alerts.history.target': 'Aan',
  'billing.alerts.kind.low_balance': 'Lae saldo',
  'billing.alerts.kind.depleted': 'Leeg',
  'billing.alerts.kind.recovered': 'Herstel',
  'billing.alerts.kind.auto_topup_succeeded': 'Outomatiese aanvulling geslaag',
  'billing.alerts.kind.auto_topup_failed': 'Outomatiese aanvulling misluk',
  'billing.alerts.channel.email': 'E-pos',
  'billing.alerts.channel.whatsapp': 'WhatsApp',
  'billing.alerts.channel.webhook': 'Webhaak',
  'billing.alerts.channel.in_app': 'Konsole',
  'billing.alerts.status.sent': 'Gestuur',
  'billing.alerts.status.failed': 'Misluk',
  'billing.alerts.status.skipped': 'Oorgeslaan',
  'billing.alerts.footnote': 'Waarskuwings volg dieselfde grootboek wat die gebruikbladsy lees.',
  'billing.alerts.daily.threshold': 'Sê my wanneer ek meer as dit op een dag bestee ({{currency}})',
  'billing.alerts.daily.thresholdHint': 'Vang \'n weghol-bot of \'n uitsending groter as bedoel. Leeg skakel dit af. Dae loop op UTC.',
  'billing.alerts.kind.daily_spend': 'Daaglikse besteding',
  'billing.statements.generate': 'Staat vir {{period}}',
  'billing.statements.hint': '\'n Gebruikstaat is \'n betaalde belastingfaktuur vir een maand se verbruik, een lyn per item, BTW gewys. Die platform reik een uit wanneer \'n maand sluit; druk die knoppie as dit nog nie hier is nie.',
  'billing.statements.created': 'Staat {{number}} is gereed.',
  'billing.statements.exists': 'Staat {{number}} is reeds uitgereik.',
  'billing.statements.noUsage': 'Niks is in {{period}} gehef nie, dus is daar geen staat nie.',
  'billing.invoiceKind.usage_statement': 'Gebruikstaat',
  'billing.usage.rates.vatOn': 'Pryse sluit {{label}} teen {{pct}}% in (registrasienr. {{number}}): wat jy sien, is wat jy betaal.',
  'billing.usage.rates.vatOff': 'Dit is die volle bedrae: geen BTW word daarby gevoeg nie.',
  'billing.explain.vat': 'BTW',
  'billing.explain.vatLine': '{{excl}} + BTW {{vat}} ({{pct}}%)',
  'billing.plan.title': 'Jou plan',
  'billing.plan.none': 'Nog geen plan nie. Jy betaal per boodskap uit jou krediete, dieselfde as die Gratis-plan. \'n Betaalde plan voeg elke maand krediete by, met meer kletsbotte, nommers, mense en kletsbotvloeie.',
  'billing.plan.current': 'Jy is op {{name}}.',
  'billing.plan.renews': 'Volgende heffing {{date}}.',
  'billing.plan.seePlans': 'Vergelyk planne',
  'billing.plan.changePlan': 'Verander plan',
  'billing.plan.modalTitle': 'Planne',
  'billing.plan.modalBody': 'Planne word op die 1ste van elke maand op jou kaart gehef. Die krediete wat ’n plan insluit, word met elke heffing by jou beursie gevoeg en word eerste gebruik, vir boodskappe. Hoe hoër die plan, hoe minder kos elke boodskap.',
  'billing.plan.free': 'Gratis',
  'billing.plan.perMonth': '{{amount}} / maand',
  'billing.plan.includedCredit': '{{amount}} elke maand',
  'billing.plan.currentPlan': 'Huidige plan',
  'billing.plan.choose': 'Kies {{name}}',
  'billing.plan.confirm.title': 'Skakel oor na {{name}}?',
  'billing.plan.confirm.subscribe': '{{now}} word nou op jou kaart gehef vir die res van hierdie maand, met {{nowCredit}}. Vanaf {{next}} word {{amount}} op die 1ste van elke maand op dieselfde kaart gehef, elke keer met {{credit}}. As jy geen kaart gestoor het nie, voeg jy een op die volgende blad by.',
  'billing.plan.confirm.subscribeFree': 'Geen maandelikse fooi nie. Jy betaal steeds per boodskap uit jou beursie.',
  'billing.plan.confirm.change': 'Die ongebruikte deel van jou huidige plan word van die prys van die nuwe een afgetrek vir die dae wat in hierdie maand oor is, en die verskil word op jou kaart gehef. Ingeslote boodskapkrediet skuif op dieselfde manier. Elke plan word op die 1ste gehef.',
  'billing.plan.confirm.action': 'Skakel plan oor',
  'billing.plan.switched': 'Jy is nou op {{name}}.',
  'billing.plan.failed': 'Kon nie die plan verander nie',
  'billing.plan.loadError': 'Kon nie die planne laai nie.',
  'billing.credits.unit.one': 'krediet',
  'billing.credits.unit.other': 'krediete',
  'billing.creditValue': 'Kredietwaarde',
  'billing.creditValue.value': '1 krediet = R1',
  'billing.creditValue.hint': 'Koop krediete in rand. Elke boodskap en antwoord word in krediete geprys.',
  'billing.topup.payIn': 'Betaal in',
  'billing.topup.buysCredits': '{{amount}} koop {{credits}}.',
  'billing.topup.youGet': 'Jy kry',
  'billing.topup.rateNote': 'Teen die huidige koers van {{rate}} {{currency}} per krediet.',
  'billing.alerts.low.emailPlaceholder': 'finance@example.com, owner@example.com',
  'billing.alerts.low.msisdnPlaceholder': '+27 82 000 1234',
  'billing.category.meta': 'Messenger en Instagram',
  'billing.referredBy.title': 'Deur \'n vennoot verwys',
  'billing.referredBy.body': '{{name}} het jou aan RepletoryReach bekendgestel. Dit kos jou niks en verander nie een van jou pryse nie.',
  'billing.referredBy.askTitle': 'Is jy deur iemand verwys?',
  'billing.referredBy.askBody': 'As \'n vennoot jou aan RepletoryReach bekendgestel het, sit hul kode in en hulle kry krediet daarvoor. Jou eie pryse verander nie.',
  'billing.referredBy.codeLabel': 'Verwysingskode',
  'billing.referredBy.codeHint': 'Agt letters en syfers, van die persoon wat jou bekendgestel het.',
  'billing.referredBy.submit': 'Voeg die kode by',
  'billing.referredBy.thanks': 'Dankie. Hul kode is op jou rekening.',
  'billing.referredBy.failed': 'Daardie kode kon nie bygevoeg word nie.',
  'billing.ledger.refundedTotal': 'Terugbetaal',
  'billing.ledger.planFeesTotal': 'Planfooie',
  'billing.ledger.spentHint': 'Boodskappe, KI en berging',
  'billing.ledger.planFeesHint': 'Jou intekening, min enige krediet vir ’n planverandering',
  'billing.ledger.addedHint': 'Aanvullings, toekennings en krediete wat by jou plan ingesluit is',
  'billing.ledger.totalsNote': 'Bygevoeg, terugbetaal en per kaart betaal, min bestee en planfooie, is jou saldo wanneer geen filter toegepas is nie.',
  'billing.ledger.exportFailed': 'Die grootboek kon nie uitgevoer word nie. Probeer weer.',
  'billing.ledger.exporting': 'Voer uit…',
  'billing.plan.limits.title': 'Wat dit toelaat',
  'billing.plan.limit.bots': 'Kletsbotte',
  'billing.plan.limit.numbers': 'WhatsApp-nommers',
  'billing.plan.limit.team_members': 'Spanlede',
  'billing.plan.limit.flows': 'Kletsbotvloeie',
  'billing.plan.limit.knowledge_documents': 'Kennisbasisdokumente',
  'billing.plan.limit.unlimited': 'Onbeperk',
  'billing.wallet.bought': 'Gekoop: {{amount}}',
  'billing.wallet.fromPlan': 'Van jou plan: {{amount}}',
  'billing.wallet.split.info': 'Krediete van jou plan word eerste gebruik en is vir boodskappe. Jou plan word per kaart betaal, nooit uit jou krediet nie.',
  'billing.wallet.split.infoLabel': 'Oor gekoopte krediete en krediete van jou plan',
  'billing.plan.prices.title': 'Boodskappryse, in krediete',
  'billing.plan.prices.marketing': 'Bemarkingsboodskap',
  'billing.plan.prices.utility': 'Nutsboodskap',
  'billing.plan.prices.service': 'Antwoord binne 24 uur',
  'billing.plan.prices.cheaper': 'Laer boodskappryse as wat jy nou betaal',
  'billing.plan.refused.title': 'Nie genoeg gekoopte krediete vir {{name}} nie',
  'billing.plan.refused.body': '\'n Plan se fooi word betaal uit krediete wat jy gekoop het. Gekoopte krediete oor: {{bought}}. Benodig: {{required}}. Die {{included}} van jou plan kan net aan boodskappe bestee word.',
  'billing.plan.refused.next': 'Laai {{shortfall}} of meer op en kies {{name}} dan weer.',
  'billing.usage.rates.view': 'Watter pryse om te wys',
  'billing.usage.rates.view.mine': 'Jou pryse',
  'billing.usage.rates.view.compare': 'Vergelyk planne',
  'billing.usage.rates.changePlan': 'Verander plan →',
  'billing.usage.rates.compareHint': 'Wat een eenheid op elke plan kos, in krediete. Jou plan is gemerk.',
  'billing.usage.rates.yourPlan': 'Jou plan',
  'billing.usage.rates.yourPlanPrice': 'Jou {{plan}}-prys',
  'billing.usage.rates.payg': 'Betaal-soos-jy-gebruik-prys:',
  'billing.usage.rates.planNote': 'Jy is op {{plan}}. Waar jou prys laer is as die betaal-soos-jy-gebruik-prys, word daardie prys deurgehaal langs joune gewys.',
  'billing.explain.planPrice': 'Gehef teen jou {{plan}}-prys (betaal-soos-jy-gebruik is {{list}})',
  'billing.explain.planPriceUnnamed': 'Gehef teen jou plan se prys (betaal-soos-jy-gebruik is {{list}})',
  'billing.topup.providerDisabled': 'Daardie betaalmetode is pas afgeskakel. Kies ŉ ander een, of probeer later weer.',
  'billing.alerts.methods.unavailable.title': 'ŉ Kaart kan nie nou gestoor word nie',
  'billing.alerts.methods.unavailable.body': 'Kaarte wat jy reeds gestoor het, bly hier, maar outomatiese herlaaie is onderbreek totdat kaartbetalings weer aangeskakel word. Jy kan steeds met die hand herlaai.',
  'billing.topup.result.checking.title': 'Ons bevestig jou betaling',
  'billing.topup.result.checking.body': 'Ons wag dat {{provider}} dit bevestig. Dit neem gewoonlik net ŉ paar sekondes.',
  'billing.topup.result.checking.bodyPlain': 'Dit neem gewoonlik net ŉ paar sekondes.',
  'billing.topup.result.slow.title': 'Steeds besig om te bevestig',
  'billing.topup.result.slow.body': '{{provider}} het jou betaling nog nie bevestig nie. Jou krediete verskyn sodra dit gebeur, so jy kan dit maar toemaak.',
  'billing.topup.result.success.title': 'Betaling suksesvol',
  'billing.topup.result.success.body': '{{credits}} is by jou beursie gevoeg.',
  'billing.topup.result.success.receipt': 'ŉ Kwitansie is op pad na jou e-pos.',
  'billing.topup.result.failed.title': 'Betaling nie voltooi nie',
  'billing.topup.result.failed.body': '{{provider}} het hierdie betaling nie voltooi nie, so geen krediete is bygevoeg nie. As daar geld van jou rekening af is, kontak ons met die verwysing hieronder.',
  'billing.topup.result.paid': 'Betaal',
  'billing.topup.result.added': 'Krediete bygevoeg',
  'billing.topup.result.balance': 'Saldo nou',
  'billing.topup.result.reference': 'Verwysing {{reference}}',
  'billing.topup.result.tryAgain': 'Probeer weer',
  'billing.topup.result.done': 'Klaar',
  'billing.unit.10k_tokens.one': 'tienduisend tekens',
  'billing.unit.10k_tokens.other': 'tienduisend tekens',
  'billing.category.voice': 'Telefoonoproepe',
  'billing.source.voice': 'Telefoonoproepe',
  'billing.unit.minute.one': 'minuut',
  'billing.unit.minute.other': 'minute',
  'billing.unit.number.one': 'nommer',
  'billing.unit.number.other': 'nommers',
  'billing.unit.month.one': 'maand',
  'billing.unit.month.other': 'maande',
  'billing.wallet.held': 'Gehou vir oproepe wat aan die gang is: {{amount}}',
  'billing.wallet.heldHint': 'Krediet wat opsy gesit is vir ’n oproep wat aan die gang is, of ’n fooi wat op goedkeuring wag. Wat nie gebruik word nie, kom terug wanneer dit eindig.',
  'billing.plan.ends': 'Gekanselleer: dit bly tot {{date}} en word nie weer gehef nie.',
  'billing.plan.keep': 'Hou my plan',
  'billing.plan.kept': 'Jy hou {{name}}. Dit hernu soos voorheen.',
  'billing.plan.cancelScheduled': 'Jou plan bly tot {{date}}, dan skuif jy na die gratis plan. Jy word nie weer gehef nie.',
  'billing.plan.confirm.cancelAtEnd': 'Jou {{name}}-plan bly soos dit is tot {{date}}, die einde van die maand waarvoor jy betaal het, en word nie weer gehef nie. Dan skuif jy na {{free}} en betaal-soos-jy-gebruik-pryse. Niks word uitgevee nie, en jy kan die plan enige tyd voor dan hou.',
  'billing.plan.confirm.subscribeFull': '{{amount}} word nou op jou kaart gehef vir hierdie maand, met {{credit}}. Daarna word dit op die 1ste van elke maand op dieselfde kaart gehef. As jy geen kaart gestoor het nie, voeg jy een op die volgende blad by.',
  'billing.plan.limit.own_whatsapp_account': 'Jou eie WhatsApp-rekening',
  'billing.plan.limit.included': 'Ingesluit',
  'billing.plan.limit.notIncluded': 'Nie ingesluit nie',
  'billing.usage.ownAccountNote': 'Boodskappe op jou eie WhatsApp-rekening word deur Meta gefaktureer, nie in krediete nie.',
  'billing.plan.limit.booking_staff': 'Personeel wat besprekings neem',
  'billing.plan.limit.booking_services': 'Dienste om te bespreek',
  'billing.plan.limit.calendar_sync': 'Kalendersinkronisasie',
  'billing.plan.limit.ticket_types': 'Kaartjietipes',
  'billing.plan.limit.ticket_automations': 'Kaartjie-outomatisering',
  'billing.card.title': 'Kaart vir jou plan',
  'billing.card.description': 'Jou plan word op die 1ste van elke maand op hierdie kaart gehef. Krediete wat jy koop, is vir boodskappe en ander gebruik, en betaal nooit vir die plan nie.',
  'billing.card.add': 'Voeg ’n kaart by',
  'billing.card.replace': 'Vervang kaart',
  'billing.card.current': '{{card}}, bygevoeg {{date}}',
  'billing.card.missing': 'Jou plan het geen kaart om te hef nie. Voeg een voor die 1ste by sodat jou plan aanhou.',
  'billing.card.none': 'Nog geen kaart gestoor nie. Jy voeg een by wanneer jy ’n betaalde plan kies.',
  'billing.card.checkHint': 'Om ’n kaart by te voeg of te vervang, maak ’n veilige betaalblad oop vir ’n klein betaling wat die kaart stoor. Daardie betaling gaan in jou krediet, so niks gaan verlore nie.',
  'billing.card.declinedTitle': 'Jou kaart is geweier',
  'billing.card.useAnother': 'Gebruik ’n ander kaart',
  'billing.card.result.planTitle': 'Betaling suksesvol',
  'billing.card.result.planBody': 'Jou plan is aan, en jou kaart is gestoor vir die 1ste van elke maand.',
  'billing.card.result.cardTitle': 'Kaart gestoor',
  'billing.card.result.cardBody': 'Jou plan word van nou af op hierdie kaart gehef. Die {{credits}} wat jy betaal het, is by jou krediet gevoeg.',
  'billing.plan.changesTo': 'Verander op {{date}} na {{name}}.',
  'billing.plan.changeScheduled': 'Jy skuif op {{date}} na {{name}}. Tot dan hou jy jou huidige plan.',
  'billing.plan.confirm.downgrade': 'Jy hou jou huidige plan tot {{date}}, want vir hierdie maand is reeds betaal. Dan skuif jy na {{name}}, en sy prys word op die 1ste van elke maand op jou kaart gehef.',
  'billing.card.ending': '{{brand}} wat eindig op {{last4}}',
  'billing.card.savedWith': 'Jou kaart wat by {{provider}} gestoor is',
  'billing.card.genericBrand': 'Kaart',
  'billing.ledger.paidByCardTotal': 'Per kaart betaal',
  'billing.ledger.paidByCardHint': 'Planfooie wat per kaart betaal is, sodat jou krediet nie gebruik is nie',
  'billing.source.ai_api': 'KI-gereedskap',
  'billing.unit.1k_chars.one': 'duisend karakters',
  'billing.unit.1k_chars.other': 'duisend karakters',
  'billing.unit.image.one': 'prent',
  'billing.unit.image.other': 'prente',
  'billing.plan.byInvoice.badge': 'Per faktuur gehef',
  'billing.plan.byInvoice.body': 'Jy betaal per faktuur vir jou plan: sy fooi word op die 1ste van jou krediet afgetrek, sodra jou betaling ons bereik het. Kontak RepletoryReach om jou plan te verander of te kanselleer.',
  'billing.pastDue.invoiceBody': 'Jou krediet het nie hierdie maand se planfooi gedek nie. Niks is afgeskakel nie. Betaal jou faktuur, en sodra jou betaling ons bereik en by jou krediet gevoeg is, hernu die plan binne die uur.',
  'billing.extra.title': 'Ekstra gebruik',
  'billing.extra.description': 'Hou aan wanneer jou krediet op is. Alles werk voort teen jou plan se pryse, tot by ’n maandelikse limiet wat jy stel, en word op jou kaart gehef.',
  'billing.extra.badge.paused': 'Onderbreek',
  'billing.extra.notOnPlan': 'Beskikbaar op {{plans}}.',
  'billing.extra.notOnPlanDefault': 'Beskikbaar op Growth en Scale.',
  'billing.extra.noCard': 'Ekstra gebruik word op ’n gestoorde kaart gehef. Voeg eers ’n kaart by en skakel dit dan hier aan.',
  'billing.extra.addCard': 'Voeg ’n kaart by',
  'billing.extra.switch': 'Hou aan wanneer my krediet op is',
  'billing.extra.switchHint': 'Gehef van {{card}}.',
  'billing.extra.limit.label': 'Maandelikse limiet ({{currency}})',
  'billing.extra.limit.hint': 'Die meeste wat ekstra gebruik in ’n maand kan bereik, van {{min}} tot {{max}}.',
  'billing.extra.mode.label': 'Hoe om te betaal',
  'billing.extra.mode.steps': 'In stappe',
  'billing.extra.mode.steps.hint': 'Jou kaart word gehef elke keer dat ekstra gebruik die bedrag bereik wat jy kies.',
  'billing.extra.mode.upfront': 'Vooruit',
  'billing.extra.mode.upfront.hint': 'Jou kaart koop krediet wanneer jou saldo laag raak, sodat jou saldo nooit onder nul gaan nie.',
  'billing.extra.mode.month_end': 'Op die 1ste, saam met jou plan',
  'billing.extra.mode.month_end.hint': 'Wat jy gebruik het, word op die 1ste van die volgende maand saam met jou plan op jou kaart gehef.',
  'billing.extra.step.label': 'Stapbedrag ({{currency}})',
  'billing.extra.step.hint': 'Jou kaart word elke keer soveel gehef. Minstens {{min}}, en nie meer as jou maandelikse limiet nie.',
  'billing.extra.topup.label': 'Aanvullingsbedrag ({{currency}})',
  'billing.extra.topup.hint': 'Hoeveel krediet jou kaart elke keer koop. Minstens {{min}}, en nie meer as jou maandelikse limiet nie.',
  'billing.extra.below.label': 'Laai op wanneer die saldo daal onder (krediete)',
  'billing.extra.below.hint': 'Jou kaart koop krediet wanneer jou saldo onder hierdie bedrag daal.',
  'billing.extra.priceNote': 'Gehef teen jou plan se pryse.',
  'billing.extra.consent': 'Wanneer jy stoor met ekstra gebruik aan, gee jy ons toestemming om jou gestoorde kaart te hef op die manier wat jy gekies het. Jy kan dit enige tyd afskakel; wat jy voor dan gebruik het, word steeds gehef.',
  'billing.extra.saved.on': 'Ekstra gebruik is aan.',
  'billing.extra.saved.off': 'Ekstra gebruik is af. Enigiets wat nog verskuldig is, word op die 1ste gehef.',
  'billing.extra.error.limitRequired': 'Vul ’n maandelikse limiet in.',
  'billing.extra.error.limitRange': 'Kies ’n limiet van {{min}} tot {{max}}.',
  'billing.extra.error.stepRequired': 'Vul ’n stapbedrag in.',
  'billing.extra.error.topupRequired': 'Vul ’n aanvullingsbedrag in.',
  'billing.extra.error.amountMin': 'Minstens {{min}}.',
  'billing.extra.error.amountOverLimit': 'Nie meer as jou maandelikse limiet nie.',
  'billing.extra.error.belowNegative': 'Gebruik 0 of meer.',
  'billing.extra.error.EXTRA_USE_NOT_ON_PLAN': 'Jou plan sluit nie ekstra gebruik in nie. Skuif op na ’n groter plan om dit aan te skakel.',
  'billing.extra.error.EXTRA_USE_NO_CARD': 'Voeg eers ’n kaart by: ekstra gebruik word op ’n gestoorde kaart gehef.',
  'billing.extra.error.EXTRA_USE_NOT_OFFERED': 'Ekstra gebruik is tans nie beskikbaar nie.',
  'billing.extra.error.EXTRA_USE_BLOCKED': 'Ekstra gebruik is vir jou rekening afgeskakel. Kontak ondersteuning om daaroor te praat.',
  'billing.extra.error.EXTRA_USE_PAST_DUE': 'Jou plan wag vir betaling. Betaal eers jou plan en skakel dan ekstra gebruik aan.',
  'billing.extra.error.forbidden': 'Net die eienaar of ’n administrateur kan ekstra gebruik verander.',
  'billing.extra.paused.title': 'Ekstra gebruik is onderbreek',
  'billing.extra.paused.declined': 'Jou kaart is geweier, dus is ekstra gebruik onderbreek en stuur stop weer by nul. Wat jy skuld, bly verskuldig.',
  'billing.extra.paused.declineReason': 'Rede gegee: {{reason}}',
  'billing.extra.paused.no_card': 'Geen kaart is gestoor nie, dus is ekstra gebruik onderbreek. Voeg ’n kaart by om dit weer aan te skakel.',
  'billing.extra.paused.past_due': 'Jou plan wag vir betaling, dus is ekstra gebruik onderbreek. Betaal jou plan om voort te gaan.',
  'billing.extra.paused.blocked': 'Ons span het ekstra gebruik vir jou rekening afgeskakel. Kontak ondersteuning om daaroor te praat.',
  'billing.extra.paused.not_on_plan': 'Jou plan sluit nie ekstra gebruik in nie, dus is dit onderbreek.',
  'billing.extra.paused.not_offered': 'Ekstra gebruik is tans nie beskikbaar nie, dus is dit onderbreek.',
  'billing.extra.paused.other': 'Ekstra gebruik is onderbreek.',
  'billing.extra.paused.payPlan': 'Betaal jou plan',
  'billing.extra.month.used': 'Hierdie maand: {{used}} van {{limit}}',
  'billing.extra.month.room': '{{room}} oor',
  'billing.extra.month.meter': 'Ekstra gebruik hierdie maand',
  'billing.extra.month.owed': 'Verskuldig: {{amount}}',
  'billing.extra.month.charged': 'Gehef: {{amount}}',
  'billing.extra.next.step': 'Volgende heffing: {{amount}}, wanneer wat jy skuld dit bereik.',
  'billing.extra.next.topup': 'Volgende heffing: {{amount}} vir krediet wanneer jou saldo onder {{below}} daal.',
  'billing.extra.next.monthEnd': 'Volgende heffing: op {{date}}, saam met jou plan.',
  'billing.extra.read.on': 'Ekstra gebruik is aan, tot {{limit}} per maand.',
  'billing.extra.read.off': 'Ekstra gebruik is af.',
  'billing.extra.read.steps': 'Betaal in stappe van {{amount}}.',
  'billing.extra.read.upfront': 'Vooruit betaal: {{amount}} vir krediet wanneer die saldo onder {{below}} daal.',
  'billing.extra.read.monthEnd': 'Betaal op die 1ste, saam met die plan.',
  'billing.extra.read.who': 'Net die eienaar of ’n administrateur kan ekstra gebruik verander.',
  'billing.extra.thisMonth': 'Ekstra gebruik hierdie maand: {{used}} van {{limit}}',
  'billing.extra.alert.title': 'Jou krediet is op, en ekstra gebruik hou alles aan die gang',
  'billing.extra.alert.body': 'Boodskappe, antwoorde en alles anders gaan voort teen jou plan se pryse. Ekstra gebruik hierdie maand: {{used}} van {{limit}}. Laai op om weer jou krediet te gebruik.',
  'billing.extra.lowNote': 'Wanneer dit op is, hou ekstra gebruik alles aan die gang.',
  'billing.extra.chipLabel': 'Ekstra gebruik',
  'billing.extra.chipAmount': '{{used}} van {{limit}}',
  'billing.plan.limit.extra_use': 'Ekstra gebruik',
  'billing.plan.limit.extra_use.hint': 'Met ’n gestoorde kaart hou jy aan nadat jou krediet op is, teen die plan se pryse, tot by ’n maandelikse limiet wat jy stel.',
  'billing.plan.limit.flows.hint': 'Beginpunte (Verwelkom nuwe klante, Terugkerende klante, Enigiets anders, Ná afhandeling) tel nie.',
  'billing.alerts.kind.extra_use_started': 'Ekstra gebruik het begin',
  'billing.alerts.kind.extra_use_charged': 'Ekstra gebruik gehef',
  'billing.alerts.kind.extra_use_declined': 'Kaart vir ekstra gebruik geweier',
  'billing.alerts.kind.extra_use_limit': 'Limiet vir ekstra gebruik bereik',
  'billing.plan.extraUsePending': '’n Betaling vir ekstra gebruik word nog bevestig. Probeer oor ’n paar minute weer.'
};

export default billing;
